/**
 * The way out of the guide, always on screen.
 *
 * The day-1 rail deliberately locks the screen down — every step blocks clicks
 * outside its spotlight — which means that without a permanent exit the only
 * escape is reloading the page. This sits above the whole rail (including the
 * spotlight overlay and the wait locks) so it is reachable at every step.
 *
 * Skipping is permanent and account-wide: it marks every one-time explainer as
 * seen so none of them can be replayed for their rewards. That is worth one
 * confirmation, since a stray tap during a guided step would otherwise end the
 * guide for good.
 */

import React, { useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { confirm } from '../confirm-modal'
import { useFtueGuideChoice, useFtueReady } from '../../hooks/useFtueGate'
import { useFtueDayOneActive } from '../../hooks/useFtueNavVisibility'

/**
 * Signup is not the guide. This component is mounted globally (the rail is not),
 * so unlike the spotlights it has to exclude these routes itself — offering to
 * skip a guide that has not started reads as a bug.
 */
const SIGNUP_ROUTES = ['/start', '/select-main-character']

/** Above the spotlight overlay (20000) so the rail can never cover it. */
const SKIP_Z = 20_100
/** The confirm must clear the button, or it opens behind its own trigger. */
const SKIP_CONFIRM_Z = 20_200

export const FtueSkipButton: React.FC = () => {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const ready = useFtueReady()
  const { over, blocking, decline } = useFtueGuideChoice()
  const dayOneActive = useFtueDayOneActive()

  const onSkip = useCallback(() => {
    confirm.open({
      title: t('ftue.skip.confirmTitle'),
      message: t('ftue.skip.confirmBody'),
      confirmText: t('ftue.skip.confirmYes'),
      cancelText: t('ftue.skip.confirmNo'),
      zIndex: SKIP_CONFIRM_Z,
      onConfirm: decline,
    })
  }, [decline, t])

  // Hidden while the opt-in prompt is up — that dialog carries its own decline,
  // and two ways to say no on one screen is just noise.
  if (!ready || over || blocking || !dayOneActive) return null
  if (SIGNUP_ROUTES.includes(pathname)) return null

  return createPortal(
    <button
      type="button"
      // Whitelisted by FtueSpotlight's click-blocker; without this class the
      // rail swallows the click and the button looks broken.
      className="ftue-skip-button"
      onClick={onSkip}
      style={{
        position: 'fixed',
        top: 'max(10px, env(safe-area-inset-top, 0px))',
        right: 'max(10px, env(safe-area-inset-right, 0px))',
        zIndex: SKIP_Z,
        padding: '6px 14px',
        borderRadius: 999,
        border: '1px solid rgba(212, 175, 55, 0.55)',
        background: 'rgba(24, 18, 10, 0.72)',
        color: '#e8d9a8',
        font: "600 13px/1.2 'Tilt_Warp', sans-serif",
        letterSpacing: '0.04em',
        cursor: 'pointer',
        // No backdrop-filter: overlays stay GPU-cheap.
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.45)',
        transition: 'opacity 160ms ease, transform 160ms ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.opacity = '0.85'
        e.currentTarget.style.transform = 'translateY(-1px)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.opacity = '1'
        e.currentTarget.style.transform = 'none'
      }}
    >
      {t('ftue.skip.button')}
    </button>,
    document.body,
  )
}
